import { createSelector } from 'reselect';
import queryString from 'query-string';
import get from 'lodash/get';
import values from 'lodash/values';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import keys from 'lodash/keys';
import {
  getReports,
  getQuery,
  getUi,
  getPlaces,
  getUsers,
  getCurrentUser,
} from './';

export const selectPlaceReports = createSelector(
  [getUi, getReports, getQuery, getPlaces, getUsers, getCurrentUser],
  (
    { selectedPlaceId, reportsSorting },
    { reportById, placesReportsPagination, reportCategoryById },
    search,
    { equipmentById },
    { userById },
    currentUser,
  ) => {
    if (search === '') {
      search = '?';
    }

    const { page, ...parsedQuery } = queryString.parse(search);

    const query = `?${queryString.stringify({
      state: 'opened,acknowledged',
      sort: reportsSorting,
      ...parsedQuery,
    })}`;

    const reportsIds = flatten(
      values(
        get(
          placesReportsPagination,
          `[${selectedPlaceId}][${query}].idsByPage`,
        ),
      ),
    );

    if (!reportsIds) {
      return [];
    }

    return reportsIds.map(id => {
      const report = reportById[id];

      return {
        ...report,
        equipment: equipmentById[report.equipmentId],
        category: reportCategoryById[report.category],
        author:
          report.author !== currentUser.id
            ? userById[report.author]
            : {
                ...userById[report.author],
                ...currentUser.currentIdentity,
              },
      };
    });
  },
);

export const selectPlaceReportsGroupedByEquipments = createSelector(
  [selectPlaceReports, getPlaces],
  (reports, { equipmentById }) => {
    const reportsGroupedByEquipmentId = groupBy(reports, 'equipmentId');

    return keys(reportsGroupedByEquipmentId).map(equipmentId => ({
      equipmentId,
      equipment: equipmentById[equipmentId],
      reports: reportsGroupedByEquipmentId[equipmentId],
    }));
  },
);

export const selectPlaceReportsPagination = createSelector(
  [getReports, getQuery, getUi],
  (
    { placesReportsPagination },
    search,
    { selectedPlaceId, reportsSorting },
  ) => {
    if (!search) {
      search = '?';
    }

    const { page, ...parsedQuery } = queryString.parse(search);

    const query = `?${queryString.stringify({
      state: 'opened,acknowledged',
      sort: reportsSorting,
      ...parsedQuery,
    })}`;

    return get(placesReportsPagination, `${selectedPlaceId}[${query}]`) || {};
  },
);
